import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Loader2, Sparkles } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useLifely } from '../context/LifelyContext';
import UpgradeModal from '../components/billing/UpgradeModal';
import { STATUS_LABELS } from '../store/mockData';

const MapsOverview = () => {
  const { maps, getEntriesForMap, fetchMaps, loading, isAuthenticated, user } = useLifely();
  const [showUpgrade, setShowUpgrade] = useState(false);
  const canUseAI = ['standard', 'plus'].includes(user?.plan);
  const isTrial = user?.plan === 'trial';

  // Fetch maps data when component mounts
  useEffect(() => {
    if (isAuthenticated) {
      fetchMaps();
    }
  }, [isAuthenticated, fetchMaps]);

  const getStatusClasses = (status) => {
    switch (status) {
      case 'configured':
        return 'bg-primary/10 text-primary border-primary/20';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  if (loading.maps) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="maps-overview-page">
      {/* Header */}
      <section className="space-y-2">
        <h1 className="text-3xl sm:text-4xl font-heading font-semibold text-foreground">
          Maps
        </h1>
        <p className="text-muted-foreground text-base max-w-2xl">
          Organize different areas of your life. Each map helps you document what exists and your intentions, without storing sensitive details.
        </p>
      </section>

      {/* AI Assistant banner — Standard-only (trial sees a gated badge) */}
      {(canUseAI || isTrial) && (
        <div className="flex items-center justify-between gap-3 rounded-xl bg-primary/5 border border-primary/20 px-4 py-3 max-w-6xl mx-auto" data-testid="maps-ai-banner">
          <div className="flex items-center gap-3 text-sm text-foreground">
            <Sparkles className="w-5 h-5 text-primary flex-shrink-0" />
            <span>AI Assistant - describe any asset in plain English and we&apos;ll fill in the map entry for you.</span>
          </div>
          {canUseAI ? (
            <Link
              to="/ai-assist"
              className="text-sm font-medium text-primary hover:underline whitespace-nowrap shrink-0"
              data-testid="maps-ai-try"
            >
              Try it
            </Link>
          ) : (
            <button type="button" onClick={() => setShowUpgrade(true)} className="shrink-0" data-testid="maps-ai-badge">
              <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20 cursor-pointer whitespace-nowrap">
                Standard feature
              </Badge>
            </button>
          )}
        </div>
      )}

      {/* Maps Grid - regular maps */}
      <section 
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto"
        data-testid="maps-grid"
      >
        {maps.map((map, index) => {
          const entriesCount = map.entryCount || getEntriesForMap(map.id).length;

          return (
            <Link
              key={map.id}
              to={map.isLink ? map.linkTo : `/maps/${map.id}`}
              data-testid={`map-card-${map.id}`}
              className={`animate-fade-in stagger-${index + 1}`}
              style={{ opacity: 0 }}
            >
              <Card className="map-card card-hover border border-border cursor-pointer group h-full overflow-hidden">
                {/* Cover Image */}
                <div className="relative h-36 overflow-hidden">
                  <img 
                    src={map.image} 
                    alt={map.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                  {map.id === 'crypto' && (
                    <div className="absolute inset-0 bg-primary/20" />
                  )}
                  <Badge
                    variant="outline"
                    className={`absolute top-3 right-3 text-xs ${getStatusClasses(map.status)}`}
                  >
                    {STATUS_LABELS[map.status]}
                  </Badge>
                </div>

                <CardContent className="p-5 space-y-3">
                  <div>
                    <h3 className="font-heading font-medium text-foreground text-lg leading-tight group-hover:text-primary transition-colors duration-200">
                      {map.name}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                      {map.description}
                    </p>
                  </div>

                  <div className="flex items-center justify-between pt-2">
                    {!map.isLink && (
                      <span className="text-xs text-muted-foreground">
                        {entriesCount} {entriesCount === 1 ? 'entry' : 'entries'}
                      </span>
                    )}
                    <div className="flex items-center gap-1 text-primary opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <span className="text-xs font-medium">Open Map</span>
                      <ArrowRight className="w-3.5 h-3.5" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </section>

      {/* Helper Note */}
      <section className="mt-8">
        <div className="bg-muted/50 rounded-xl p-6 border border-border">
          <p className="text-sm text-muted-foreground leading-relaxed">
            <span className="font-medium text-foreground">Privacy note:</span> Do not enter passwords, private keys, seed phrases, or credentials in any map. 
            Focus on documenting what exists and where it&apos;s managed, not how to access it.
          </p>
        </div>
      </section>

      <UpgradeModal isOpen={showUpgrade} onClose={() => setShowUpgrade(false)} trigger="ai" />
    </div>
  );
};

export default MapsOverview;
